/**
 * @file DeliveryOutcomeEditor.tsx
 * @description Форма условий доставки во вкладке «Доставка»: что согласуем,
 * расчёт перевозчика, итог и действия (phase19.24 шаг 5.2, §3).
 *
 * Пришла на смену `DeliveryAgreementOutcomeForm`: та жила над вкладками и не
 * знала ни про расчёт, ни про снапшот условий. Поля исхода рисует общий с
 * созданием заказа `ReceiptMethodFields` — формулировки в двух местах
 * совпадают по определению.
 *
 * Раскладка §3: на `≥lg` две колонки (форма слева, итог и действия справа),
 * на `<lg` ИТОГ поднимается НАД формой — менеджер диктует цифры по телефону
 * и ищет их первым делом.
 *
 * Состояние формы и сохранение живут в `useDeliveryOutcomeForm`: здесь только
 * раскладка и передача слотов.
 */

import { useState } from "react";

import { DeliveryActionsPanel } from "./DeliveryActionsPanel";
import { DeliveryQuoteInline } from "./DeliveryQuoteInline";
import { DeliveryTotalsCard } from "./DeliveryTotalsCard";
import { useDeliveryOutcomeForm } from "./useDeliveryOutcomeForm";
import { useShopPickupLocation } from "../../../hooks/useShopPickupLocation";
import type { OrderDeliveryReceiptDto } from "../../../lib/api-delivery-agreement";
import {
  SHIPMENT_CARRIER_LABEL,
  type OrderDeliveryDto,
  type OrderPaymentTerms,
  type ShipmentCarrier,
} from "../../../lib/api-orders";
import type { ReceiptFormValue } from "../../../lib/receipt-method";
import { SectionLabel } from "../../ui/SectionLabel";
import { DeliveryQuoteSheet } from "../DeliveryQuoteSheet";
import { ReceiptMethodFields } from "../ReceiptMethodFields";

interface Props {
  orderId: string;
  /** Снапшот условий, на котором открывается форма. */
  receipt: OrderDeliveryReceiptDto | null;
  paymentTerms: OrderPaymentTerms | undefined;
  /** Сумма товаров заказа (без доставки) строкой. */
  itemsTotal: string;
  legalEntity: boolean;
  shopSlug?: string;
  /** Подпись главной кнопки из реестра состояний блока. */
  primaryLabel: string;
  /** Перевозчики с подключённой интеграцией расчёта (тип — как у сервера). */
  integratedCarriers: ShipmentCarrier[];
  /** Ответ `GET …/delivery` — источник позиций и адреса для расчёта. */
  delivery: OrderDeliveryDto | null;
  /** Заявка существует — её можно отменить. */
  canCancel: boolean;
  disabled?: boolean;
  onSaved: () => void | Promise<void>;
  /** Итог расчёта кодом причины — блок объясняет им пустую стоимость. */
  onQuoteReason?: (reason: string | null) => void;
  /** Исход сохранён, открыть подтверждение отправки (диалогом владеет блок). */
  onSend: () => void;
  onCancelRequest: () => void;
}

export function DeliveryOutcomeEditor({
  orderId,
  receipt,
  paymentTerms,
  itemsTotal,
  legalEntity,
  shopSlug,
  primaryLabel,
  integratedCarriers,
  delivery,
  canCancel,
  disabled,
  onSaved,
  onQuoteReason,
  onSend,
  onCancelRequest,
}: Props) {
  const pickup = useShopPickupLocation();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const form = useDeliveryOutcomeForm({
    orderId,
    receipt,
    paymentTerms,
    pickupReady: pickup.ready,
    onSaved,
  });

  const busy = !!disabled || form.saving;

  /**
   * Смена способа или перевозчика обесценивает прошлый отказ расчёта: иначе
   * бейдж «Расчёт не выполнен» висел бы над заказом на самовывоз.
   */
  function changeReceipt(next: ReceiptFormValue): void {
    if (next.method !== form.value.method || next.carrier !== form.value.carrier) {
      onQuoteReason?.(null);
    }
    form.changeReceipt(next);
  }

  // Блок расчёта — для перевозчика с калькулятором в системе (§8, сегодня
  // СДЭК): для остальных о калькуляторе не упоминаем вовсе. Здоровье
  // интеграции кнопку не прячет — иначе менеджер остаётся без объяснения и
  // без возможности повторить (правка 2026-09-22).
  const quoteCarrier =
    form.value.method === "CARRIER" &&
    form.value.carrier &&
    integratedCarriers.includes(form.value.carrier)
      ? form.value.carrier
      : null;
  const goodsTotal = Number(itemsTotal);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6">
      <div className="order-1 min-w-0 lg:col-start-2 lg:row-start-1">
        <DeliveryTotalsCard itemsTotal={itemsTotal} value={form.value} />
      </div>

      <div className="order-2 flex min-w-0 flex-col gap-4 lg:col-start-1 lg:row-start-1 lg:row-span-2">
        <SectionLabel as="h2">Что согласуем</SectionLabel>
        <ReceiptMethodFields
          value={form.value}
          onChange={changeReceipt}
          pickup={pickup}
          issue={form.issue}
          disabled={busy}
          shopSlug={shopSlug}
          idPrefix="delivery"
          amountHint={
            form.quote
              ? "Подставлена из выбранного тарифа — поправить вручную можно."
              : undefined
          }
          quoteSlot={
            quoteCarrier ? (
              <DeliveryQuoteInline
                quote={form.quote}
                carrierName={SHIPMENT_CARRIER_LABEL[quoteCarrier]}
                clientPrice={
                  form.value.deliveryAmount ? Number(form.value.deliveryAmount) : null
                }
                disabled={busy}
                onCalculate={() => setQuoteOpen(true)}
              />
            ) : null
          }
        />
      </div>

      <div className="order-3 min-w-0 lg:col-start-2 lg:row-start-2">
        <DeliveryActionsPanel
          value={form.value}
          paymentTerms={form.terms}
          onPaymentTermsChange={form.setTerms}
          legalEntity={legalEntity}
          itemsTotal={itemsTotal}
          pickup={pickup.location}
          primaryLabel={primaryLabel}
          blockedReason={form.blockedReason}
          canCancel={canCancel}
          disabled={busy}
          onPrimary={() => void form.save(true).then((ok) => ok && onSend())}
          onSaveDraft={() => void form.save(false)}
          onCancelRequest={onCancelRequest}
        />
      </div>

      {quoteCarrier ? (
        <DeliveryQuoteSheet
          open={quoteOpen}
          onClose={() => setQuoteOpen(false)}
          orderId={orderId}
          shopSlug={shopSlug}
          delivery={delivery}
          drafts={[]}
          mode="pick-for-outcome"
          goodsTotal={Number.isFinite(goodsTotal) ? goodsTotal : undefined}
          onQuoteReason={onQuoteReason}
          onPick={(option, value) => {
            form.applyQuote(option, value);
            setQuoteOpen(false);
            // Тариф выбран — объяснять пустую стоимость больше нечем.
            onQuoteReason?.(null);
          }}
          onApplied={() => void onSaved()}
        />
      ) : null}
    </div>
  );
}
